import type { ProductFacetsDto } from '@up2stay/up2promo-api-client';
import {
  countActiveFilters,
  EMPTY_PRODUCTS_LIST_FILTERS,
  facetHasChoice,
  facetOptions,
  intendedBehaviorToProductType,
  labelForFacetValue,
  toggleInList,
  UNKNOWN_FACET_VALUE,
  type LocationFilterOption,
  type ProductsListFiltersLabels,
  type ProductsListFilterValues,
} from '@up2stay/up2promo-commons/products';
import {
  Button,
  Checkbox,
  Drawer,
  DrawerContent,
  DrawerHeader,
  DrawerTitle,
  Label,
  Popover,
  PopoverContent,
  PopoverTrigger,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Separator,
} from '@up2stay/up2promo-ui';
import { cn } from '@up2stay/up2promo-ui';
import { ListFilter } from 'lucide-react';
import { type ReactNode, Fragment, useId, useMemo, useState } from 'react';

import { listToolbarFilterButtonClass } from '@/core/components/layout/list-toolbar-styles';
import { useIsMobile } from '@/core/hooks/use-mobile';

import { ProductTypeBadge } from './product-type-badge';

const POPOVER_SCROLL_HIDE =
  '[scrollbar-width:none] [-ms-overflow-style:none] [&::-webkit-scrollbar]:hidden';

type ProductsListFiltersProps = {
  value: ProductsListFilterValues;
  onChange: (next: ProductsListFilterValues) => void;
  facets: ProductFacetsDto | undefined;
  locationOptions: LocationFilterOption[];
  labels: ProductsListFiltersLabels;
};

function FilterCheckRow({
  checked,
  label,
  onToggle,
}: {
  checked: boolean;
  label: ReactNode;
  onToggle: () => void;
}) {
  const checkboxId = useId();
  return (
    <div className="flex items-center gap-3 rounded-lg px-2 py-2">
      <Checkbox
        id={checkboxId}
        checked={checked}
        onCheckedChange={() => onToggle()}
        className="border-border/80"
      />
      <Label
        htmlFor={checkboxId}
        className="flex min-w-0 flex-1 cursor-pointer items-center gap-2 text-left text-sm font-normal leading-snug text-foreground"
      >
        {label}
      </Label>
    </div>
  );
}

function FacetCheckSection({
  title,
  options,
  selected,
  onToggle,
  renderLabel,
}: {
  title: string;
  options: Array<{ value: string; count: number }>;
  selected: string[];
  onToggle: (value: string) => void;
  renderLabel: (value: string) => ReactNode;
}) {
  if (options.length === 0) return null;
  return (
    <section className="space-y-1">
      <h3 className="px-2 text-xs font-medium text-muted-foreground">
        {title}
      </h3>
      <div className="space-y-0.5">
        {options.map((opt) => (
          <FilterCheckRow
            key={opt.value}
            checked={selected.includes(opt.value)}
            label={renderLabel(opt.value)}
            onToggle={() => onToggle(opt.value)}
          />
        ))}
      </div>
    </section>
  );
}

function FilterPanelBody({
  value,
  onChange,
  facets,
  locationOptions,
  labels,
  onRequestClose,
  showDoneAction,
  constrainHeight,
}: ProductsListFiltersProps & {
  onRequestClose?: () => void;
  showDoneAction: boolean;
  constrainHeight: boolean;
}) {
  const locationSelectId = useId();
  const activeCount = countActiveFilters(value);

  const behaviourOpts = facetOptions(facets?.behaviors);
  const formOpts = facetOptions(facets?.forms);
  const materialOpts = facetOptions(facets?.materials);
  const colorOpts = facetOptions(facets?.colors);
  const sizeOpts = facetOptions(facets?.sizes);
  const localeOpts = facetOptions(facets?.languages);

  const locationFacetIds = useMemo(() => {
    const ids = new Set(
      facetOptions(facets?.locationIds)
        .map((f) => f.value)
        .filter((v) => v && v !== UNKNOWN_FACET_VALUE),
    );
    return locationOptions.filter((loc) => ids.has(loc.id));
  }, [facets?.locationIds, locationOptions]);

  const sections: ReactNode[] = [];

  if (facetHasChoice(behaviourOpts, value.behaviors)) {
    sections.push(
      <FacetCheckSection
        key="behaviour"
        title={labels.sectionBehaviour}
        options={behaviourOpts}
        selected={value.behaviors}
        onToggle={(v) =>
          onChange({
            ...value,
            behaviors: toggleInList(value.behaviors, v),
          })
        }
        renderLabel={(v) => {
          if (v === UNKNOWN_FACET_VALUE) return labels.unknown;
          const short = intendedBehaviorToProductType(v);
          const text = labels.behaviourLabels[v] ?? v;
          if (!short) return text;
          return (
            <ProductTypeBadge
              productType={short}
              label={text}
              className="pointer-events-none"
            />
          );
        }}
      />,
    );
  }

  if (facetHasChoice(formOpts, value.forms)) {
    sections.push(
      <FacetCheckSection
        key="form"
        title={labels.sectionForm}
        options={formOpts}
        selected={value.forms}
        onToggle={(v) =>
          onChange({
            ...value,
            forms: toggleInList(value.forms, v),
          })
        }
        renderLabel={(v) =>
          labelForFacetValue(
            v,
            labels.formLabels as Record<string, string>,
            labels.unknown,
          )
        }
      />,
    );
  }

  if (facetHasChoice(materialOpts, value.materials)) {
    sections.push(
      <FacetCheckSection
        key="material"
        title={labels.sectionMaterial}
        options={materialOpts}
        selected={value.materials}
        onToggle={(v) =>
          onChange({
            ...value,
            materials: toggleInList(value.materials, v),
          })
        }
        renderLabel={(v) =>
          labelForFacetValue(
            v,
            labels.materialLabels as Record<string, string>,
            labels.unknown,
          )
        }
      />,
    );
  }

  if (facetHasChoice(colorOpts, value.colors)) {
    sections.push(
      <FacetCheckSection
        key="color"
        title={labels.sectionColor}
        options={colorOpts}
        selected={value.colors}
        onToggle={(v) =>
          onChange({
            ...value,
            colors: toggleInList(value.colors, v),
          })
        }
        renderLabel={(v) => labelForFacetValue(v, undefined, labels.unknown)}
      />,
    );
  }

  if (facetHasChoice(sizeOpts, value.sizes)) {
    sections.push(
      <FacetCheckSection
        key="size"
        title={labels.sectionSize}
        options={sizeOpts}
        selected={value.sizes}
        onToggle={(v) =>
          onChange({
            ...value,
            sizes: toggleInList(value.sizes, v),
          })
        }
        renderLabel={(v) => labelForFacetValue(v, undefined, labels.unknown)}
      />,
    );
  }

  if (facetHasChoice(localeOpts, value.languages)) {
    sections.push(
      <FacetCheckSection
        key="locale"
        title={labels.sectionLocale}
        options={localeOpts}
        selected={value.languages}
        onToggle={(v) =>
          onChange({
            ...value,
            languages: toggleInList(value.languages, v),
          })
        }
        renderLabel={(v) => labelForFacetValue(v, undefined, labels.unknown)}
      />,
    );
  }

  if (locationFacetIds.length >= 2 || value.locationId) {
    sections.push(
      <section key="location" className="space-y-2">
        <Label
          htmlFor={locationSelectId}
          className="block px-2 text-xs font-medium text-muted-foreground"
        >
          {labels.sectionLocation}
        </Label>
        <div className="px-2">
          {locationFacetIds.length === 0 ? (
            <p className="py-2 text-sm text-muted-foreground">
              {labels.noLocations}
            </p>
          ) : (
            <Select
              value={value.locationId ?? '__all__'}
              onValueChange={(v) =>
                onChange({
                  ...value,
                  locationId: v === '__all__' ? null : v,
                })
              }
            >
              <SelectTrigger id={locationSelectId} className="h-9 w-full">
                <SelectValue />
              </SelectTrigger>
              <SelectContent position="popper" sideOffset={6}>
                <SelectItem value="__all__">{labels.allLocations}</SelectItem>
                {locationFacetIds.map((loc) => (
                  <SelectItem key={loc.id} value={loc.id}>
                    {loc.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          )}
        </div>
      </section>,
    );
  }

  return (
    <div
      className={cn(
        'flex flex-col',
        constrainHeight && 'max-h-[min(70vh,28rem)]',
      )}
    >
      <div
        className={cn(
          'space-y-4 px-1 py-1',
          constrainHeight &&
            cn(
              'min-h-0 flex-1 overflow-y-auto overscroll-contain',
              POPOVER_SCROLL_HIDE,
            ),
        )}
      >
        {sections.length === 0
          ? null
          : sections.map((section, index) => (
              <Fragment key={index}>
                {index > 0 ? <Separator /> : null}
                {section}
              </Fragment>
            ))}
      </div>

      <div className="mt-3 flex shrink-0 items-center justify-between gap-2 border-t border-border/60 pt-3">
        <Button
          type="button"
          variant="ghost"
          size="sm"
          className="h-8 px-2 text-muted-foreground hover:text-foreground"
          disabled={activeCount === 0}
          onClick={() => onChange(EMPTY_PRODUCTS_LIST_FILTERS)}
        >
          {labels.clearAll}
        </Button>
        {showDoneAction ? (
          <Button
            type="button"
            size="sm"
            className="h-8"
            onClick={() => onRequestClose?.()}
          >
            {labels.done}
          </Button>
        ) : null}
      </div>
    </div>
  );
}

/**
 * Whether any section would offer a real choice. Without one (e.g. no
 * products yet) the filter button is disabled instead of opening an empty
 * panel (DEV-556).
 */
function hasFilterChoices(
  facets: ProductFacetsDto | undefined,
  value: ProductsListFilterValues,
  locationOptions: LocationFilterOption[],
): boolean {
  if (countActiveFilters(value) > 0) return true;
  const knownLocationIds = new Set(
    facetOptions(facets?.locationIds)
      .map((f) => f.value)
      .filter((v) => v && v !== UNKNOWN_FACET_VALUE),
  );
  const locationsWithProducts = locationOptions.filter((loc) =>
    knownLocationIds.has(loc.id),
  );
  return (
    facetHasChoice(facetOptions(facets?.behaviors), value.behaviors) ||
    facetHasChoice(facetOptions(facets?.forms), value.forms) ||
    facetHasChoice(facetOptions(facets?.materials), value.materials) ||
    facetHasChoice(facetOptions(facets?.colors), value.colors) ||
    facetHasChoice(facetOptions(facets?.sizes), value.sizes) ||
    facetHasChoice(facetOptions(facets?.languages), value.languages) ||
    locationsWithProducts.length >= 2
  );
}

export function ProductsListFilters({
  value,
  onChange,
  facets,
  locationOptions,
  labels,
}: ProductsListFiltersProps) {
  const isMobile = useIsMobile();
  const [open, setOpen] = useState(false);

  const activeCount = useMemo(() => countActiveFilters(value), [value]);
  const filterDisabled = useMemo(
    () => !hasFilterChoices(facets, value, locationOptions),
    [facets, value, locationOptions],
  );

  if (isMobile) {
    return (
      <>
        <Button
          type="button"
          variant="outline"
          size="lg"
          aria-label={labels.filter}
          disabled={filterDisabled}
          className={cn(
            listToolbarFilterButtonClass,
            activeCount > 0 && 'border-primary/40 bg-primary/5',
          )}
          onClick={() => setOpen(true)}
        >
          <ListFilter className="h-4 w-4 shrink-0 text-muted-foreground" />
          <span>{labels.filter}</span>
          {activeCount > 0 ? (
            <span className="flex h-5 min-w-5 items-center justify-center rounded-full bg-primary-solid px-1.5 text-xs font-semibold tabular-nums text-primary-solid-foreground">
              {activeCount}
            </span>
          ) : null}
        </Button>

        <Drawer open={open} onOpenChange={setOpen}>
          <DrawerContent
            fitContent={false}
            className="h-[min(92dvh,560px)] max-h-[min(92dvh,560px)]"
          >
            <DrawerHeader className="-mx-4 shrink-0 px-4 pb-2 pt-1 text-left">
              <DrawerTitle className="text-base">
                {labels.filtersTitle}
              </DrawerTitle>
            </DrawerHeader>
            <FilterPanelBody
              value={value}
              onChange={onChange}
              facets={facets}
              locationOptions={locationOptions}
              labels={labels}
              showDoneAction
              constrainHeight={false}
              onRequestClose={() => setOpen(false)}
            />
          </DrawerContent>
        </Drawer>
      </>
    );
  }

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          type="button"
          variant="outline"
          size="lg"
          aria-expanded={open}
          aria-label={labels.filter}
          disabled={filterDisabled}
          className={cn(
            listToolbarFilterButtonClass,
            activeCount > 0 && 'border-primary/40 bg-primary/5',
          )}
        >
          <ListFilter className="h-4 w-4 shrink-0 text-muted-foreground" />
          <span>{labels.filter}</span>
          {activeCount > 0 ? (
            <span className="flex h-5 min-w-5 items-center justify-center rounded-full bg-primary-solid px-1.5 text-xs font-semibold tabular-nums text-primary-solid-foreground">
              {activeCount}
            </span>
          ) : null}
        </Button>
      </PopoverTrigger>
      <PopoverContent
        align="end"
        sideOffset={8}
        className="w-[min(calc(100vw-2rem),20rem)] p-4"
        onOpenAutoFocus={(e) => e.preventDefault()}
      >
        <FilterPanelBody
          value={value}
          onChange={onChange}
          facets={facets}
          locationOptions={locationOptions}
          labels={labels}
          showDoneAction={false}
          constrainHeight
        />
      </PopoverContent>
    </Popover>
  );
}
